import { createApp } from 'vue';
import VueScrollTo from 'vue-scrollto';
import FloatingVue from 'floating-vue';

import * as Sentry from '@sentry/vue';

import '@/scss/app.scss';

import i18n from '@/i18n';
import store from '@/store';
import router from '@/vue/router';
import config from '@/config/client';
import { apolloProvider } from '@/apollo';
import { floatingVueConfig, registerComponents } from '@/vue/utils';
import { Auth0Plugin, AppState } from '@/auth';
import { trackingPlugin } from '@/tracking/trackingPlugin';
import { afterRouteEnterPlugin } from '@/afterRouteEnterPlugin';

import App from '@components/App.vue';
import * as commonComponents from '@components/common';
import * as gridRenderers from '@components/GridList/GridListRenderers';
import overlayWhitelist from '@directives/overlayWhitelist';
import { getLeaseManager } from './license';
import { checkForChunkLoadError, handleChunkLoadError } from '@utils/chunkLoadError';
import { config as sharedConfig } from '@common/config/shared';

const leaseManager = getLeaseManager(store);

const buildMode = BUILD_MODE;
const isDevelopmentMode = buildMode === 'development';

const trackableSentryEnvs = ['production', 'staging'];
/**
 * By default, only log to sentry in production or staging env
 * But can be overwritten, e.g. for specific dev depyloyments
 */
const trackSentry = trackableSentryEnvs.includes(config.sentryEnv) || config.trackSentry === 'true';

const googleAnalyticsRegexp =
  /(Cannot read property 'readyState' of null|Cannot read properties of null \(reading 'readyState'\)|null is not an object \(evaluating 'g.readyState'\)|g is null)/;

export const app = createApp(App);

if (trackSentry) {
  Sentry.init({
    app,
    dsn: config.sentryDsn,
    debug: config.sentryEnv !== 'production',
    logErrors: config.sentryEnv !== 'production',
    sampleRate: 0.2,
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      // For older browsers, such as Safari 11
      "Can't find variable: IntersectionObserver",
    ],
    async beforeSend(event, hint) {
      const ogException = hint.originalException;

      // ignore google analytics load error (happens when blocked by browser anti tracking)
      if (
        ogException instanceof Error &&
        googleAnalyticsRegexp.test(ogException.message) &&
        ogException.stack.includes('https://www.google-analytics.com/analytics.js')
      ) {
        return null;
      }

      // Ignore chunk load error
      const result = await checkForChunkLoadError(ogException);

      if (result === 'notFound') {
        return null;
      }

      return event;
    },
    beforeBreadcrumb(breadcrumb, hint) {
      if (breadcrumb.type === 'http' && breadcrumb.category === 'fetch' && breadcrumb.data.url === config.apiUrlFull) {
        // Filter http requests to graphql api, because we have link-sentry for that
        return null;
      }

      if (breadcrumb.category.startsWith('ui.click') && hint) {
        // Add button text to ui.click
        const target = hint.event.target;

        const element = target as Element;

        if (
          (element.nodeName === 'BUTTON' ||
            // With Btn component, element is the text wrapper
            element.parentElement?.nodeName === 'BUTTON') &&
          element.textContent
        ) {
          breadcrumb.message = `${breadcrumb.message}:text(${element.textContent})`;
        }
      }

      return breadcrumb;
    },
    environment: config.sentryEnv,
    release: config.appVersionShort,
  });

  Sentry.setTags({
    buildYear: config.buildYear,
    version: config.appVersionShort,
    acoHost: config.acoHost,
    url: config.apiUrlFull,
    buildMode,
  });

  if (config.buildId) {
    Sentry.setTag('buildId', config.buildId);
  }
}

if (isDevelopmentMode) {
  app.config.performance = DEVTOOLS;
}

app.use(router);

app.use(apolloProvider);

app.use(store);

app.use(i18n);

app.use(afterRouteEnterPlugin);

app.use(trackingPlugin, {
  mixpanelProjectToken: config.mixpanelProjectToken,
  trackSentry,
});

app.use(FloatingVue, floatingVueConfig);

app.use(VueScrollTo);

app.config.globalProperties.$config = { ...config, constants: sharedConfig };

// composition API
app.provide('$config', config);

registerComponents(app, commonComponents);
registerComponents(app, gridRenderers);

app.directive(overlayWhitelist.name, overlayWhitelist.shorthand);

const resolveConsent = (_event: Event) => {
  store.dispatch('tracking/setConsent', {
    data: {
      necessary: window.Cookiebot.consent.necessary,
      preferences: window.Cookiebot.consent.preferences,
      statistics: window.Cookiebot.consent.statistics,
      marketing: window.Cookiebot.consent.marketing,
    },
  });
};

if (window.Cookiebot) {
  window.addEventListener('CookiebotOnAccept', resolveConsent, false);
  window.addEventListener('CookiebotOnDecline', resolveConsent, false);
}

app.use(Auth0Plugin, {
  domain: config.auth0Domain,
  clientId: config.auth0ClientId,
  audience: config.auth0Audience,
  onRedirectCallback: (appState: AppState) => {
    store.commit('user/USER_SET_INVITE_TOKEN', appState.inviteToken);
    if (appState.kickLease) {
      leaseManager.kickLease();
    }
  },
});

handleChunkLoadError();

app.mount('#app');
